import type { CSSProperties } from 'react';
import {
  LIFECYCLE_PHASE_LABELS,
  type LifecyclePhase,
  type ProjectHealth,
} from '@scoutaround/script-breakdown-shared';

/**
 * The design's status palette, lifted from `12 Projects v2` rather than invented.
 *
 * Colour is never the ONLY carrier of meaning: every tag also shows its label,
 * and health shows a dot beside readable text. A producer with a colour-vision
 * difference, or reading a printed schedule, gets the same information.
 */
const LIFECYCLE_STYLE: Record<LifecyclePhase, [string, string]> = {
  IDEA: ['#F2F4F7', '#475467'],
  DEVELOPMENT: ['#EAECF0', '#344054'],
  PRE_PRODUCTION: ['#475467', '#ffffff'],
  PRODUCTION: ['#232323', '#ffffff'],
  WRAP: ['#D1FADF', '#067647'],
  // Darkened from the artboard's #98A2B3, which is 2.34:1 on this background.
  // See the note on the requirement palette below.
  ARCHIVED: ['#F2F4F7', '#667085'],
};

export function LifecycleTag({ phase }: { phase: LifecyclePhase }) {
  const [bg, fg] = LIFECYCLE_STYLE[phase];
  return (
    <span className="stag" style={{ background: bg, color: fg }} data-testid={`lifecycle-${phase}`}>
      {LIFECYCLE_PHASE_LABELS[phase]}
    </span>
  );
}

const HEALTH_DOT: Record<ProjectHealth, string> = {
  ON_TRACK: '#079455',
  AT_RISK: '#B54708',
  BLOCKED: '#D43F3F',
};

const HEALTH_LABEL: Record<ProjectHealth, string> = {
  ON_TRACK: 'On track',
  AT_RISK: 'At risk',
  BLOCKED: 'Blocked',
};

/**
 * Health, with its provenance visible.
 *
 * An overridden value is marked. A year later "was that computed or did someone
 * type it?" is exactly the question asked when a schedule slips, and a bare
 * badge cannot answer it.
 */
export function HealthTag({
  derived,
  override,
}: {
  derived: ProjectHealth;
  override?: ProjectHealth | null;
}) {
  const effective = override ?? derived;
  const style: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    fontSize: 12,
    fontWeight: 600,
  };

  return (
    <span style={style} data-testid={`health-${effective}`}>
      <span
        style={{
          width: 8,
          height: 8,
          flex: 'none',
          borderRadius: '50%',
          background: HEALTH_DOT[effective],
        }}
      />
      {HEALTH_LABEL[effective]}
      {override ? (
        <span className="mm" style={{ fontWeight: 400, fontSize: 11 }} title="Set by a person, not computed">
          · set manually
        </span>
      ) : null}
    </span>
  );
}

const SOURCING_STYLE: Record<string, [string, string]> = {
  ACTIVE: ['#D1FADF', '#067647'],
  PAUSED: ['#FDF2CC', '#B54708'],
  INACTIVE: ['#F2F4F7', '#667085'],
};

/** Sourcing is a SEPARATE axis from approval, and the UI says so plainly. */
export function SourcingTag({ status }: { status: string }) {
  const [bg, fg] = SOURCING_STYLE[status] ?? SOURCING_STYLE.INACTIVE!;
  // PLAIN ENGLISH, AND THE SAME WORDS THE BUTTONS USE. "Sourcing" is a term of
  // art the industry uses and this screen's readers did not — the badge and the
  // button beside it now say the same thing, so a producer is never translating
  // between two vocabularies to work out what state a requirement is in.
  const label =
    status === 'ACTIVE' ? 'searching' : status === 'PAUSED' ? 'search paused' : 'not searching';
  return (
    <span className="stag" style={{ background: bg, color: fg }} data-testid={`sourcing-${status}`}>
      {label}
    </span>
  );
}

const REQUIREMENT_STYLE: Record<string, [string, string]> = {
  APPROVED: ['#D1FADF', '#067647'],
  PROPOSED: ['#FDF2CC', '#B54708'],
  DRAFT: ['#F2F4F7', '#667085'],
  // #98A2B3 on #F2F4F7 is 2.34:1 — a pill nobody can read is not a muted
  // state, it is a missing one. These three carry their meaning in the LABEL
  // (this file's own rule: colour is never the only signal), so darkening them
  // to an AA-passing ratio costs the design nothing.
  DISMISSED: ['#F2F4F7', '#667085'],
  SUPERSEDED: ['#F2F4F7', '#667085'],
  ARCHIVED: ['#F2F4F7', '#667085'],
};

/**
 * The enum said out loud.
 *
 * It used to render the raw status lowercased — "proposed", "superseded" — and
 * a producer reading "proposed" cannot tell that it means the machine suggested
 * this and is waiting on THEM. These are the states a human is being asked
 * about, so they are named in the words the question is asked in.
 *
 * The `data-testid` keeps the ENUM, so no selector depends on prose.
 */
const REQUIREMENT_LABEL: Record<string, string> = {
  DRAFT: 'draft',
  PROPOSED: 'awaiting your decision',
  APPROVED: 'confirmed',
  DISMISSED: 'not needed',
  SUPERSEDED: 'replaced by a newer one',
  ARCHIVED: 'archived',
};

export function RequirementTag({ status }: { status: string }) {
  const [bg, fg] = REQUIREMENT_STYLE[status] ?? REQUIREMENT_STYLE.DRAFT!;
  return (
    <span className="stag" style={{ background: bg, color: fg }} data-testid={`req-${status}`}>
      {REQUIREMENT_LABEL[status] ?? status.toLowerCase().replace('_', ' ')}
    </span>
  );
}

const PLAN_STYLE: Record<string, [string, string]> = {
  FREE: ['#F2F4F7', '#475467'],
  LITE: ['#EAECF0', '#344054'],
  COMPANY: ['#232323', '#ffffff'],
};

/**
 * What this production is paying for, stated where its name is.
 *
 * WHY IN THE HEADER. The plan decides what half the workspace will let somebody
 * do — whether Schedule appears, whether a file can be uploaded, whether an
 * edit will be accepted at all. Leaving that one tab away meant a producer met
 * it as a refusal rather than as a fact about their production. A lapsed
 * production is the sharpest case: `EXPIRED` is the difference between "we
 * never paid" and "we stopped paying", and only one of those is read-only.
 *
 * SINCE-WHEN IS PART OF THE FACT. "Company until March" and "Company since
 * September, until March" answer different questions, and the second is the one
 * asked when somebody is deciding whether to renew.
 */
export function PlanTag({
  state,
  planCode,
  termMonths,
  periodStartsAt,
  periodEndsAt,
}: {
  state: 'FREE' | 'PAID' | 'EXPIRED';
  planCode: string;
  termMonths: number | null;
  periodStartsAt: string | null;
  periodEndsAt: string | null;
}) {
  const [bg, fg] = PLAN_STYLE[planCode] ?? PLAN_STYLE.FREE!;
  const date = (iso: string | null): string | null =>
    iso === null ? null : new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

  const started = date(periodStartsAt);
  const ends = date(periodEndsAt);

  const label =
    state === 'PAID'
      ? `${planCode}${termMonths === null ? '' : ` · ${String(termMonths)} month${termMonths === 1 ? '' : 's'}`}`
      : state === 'EXPIRED'
        ? 'Free · expired'
        : 'Free';

  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}
      data-testid={`plan-state-${state}`}
    >
      <span className="stag" style={{ background: bg, color: fg }}>
        {label}
      </span>

      {state === 'PAID' && started !== null ? (
        <span className="mm" style={{ fontSize: 11.5 }} data-testid="plan-period">
          since {started}
          {ends === null ? '' : ` · until ${ends}`}
        </span>
      ) : null}

      {state === 'EXPIRED' && ends !== null ? (
        // The date it stopped is the actionable half of an expiry.
        <span className="mm" style={{ fontSize: 11.5 }} data-testid="plan-period">
          ended {ends}
        </span>
      ) : null}
    </span>
  );
}
